import React, { Fragment } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Grid, RichText } from '@printdeal/ui-library';
import { FeatureToggle } from '@printdeal/feature-management/react';
import { Markdown } from '@printdeal/markdown/react';
import { DELIVERY_TOOLS } from '@printdeal/business-logic';
import { DeliveryToolRadioCard } from './DeliveryToolRadioCard';
import { Service } from '../../../types/product/productDetailPage';
import type { SelectedDeliveryTool } from '../../../types/file-delivery/FileDelivery';

interface Props {
  isUploaderEnabled: boolean;
  isEditorEnabled: boolean;
  isAltUploadBeforeOrderEnabled: boolean;
  onToolChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedDeliveryTool?: SelectedDeliveryTool;
  servicesEnabled: boolean;
  isFileDesignServiceEnabled?:boolean;
  fileDesignService?: Service;
  handleServiceSelection?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isFileDesignServiceSelected?: boolean;
  isFileChangeServiceEnabled?: boolean;
  isFileChangeServiceSelected?: boolean;
  fileChangeService?: Service;
}

const DetermineDeliveryTool = ({
  isUploaderEnabled = false,
  isEditorEnabled = false,
  isAltUploadBeforeOrderEnabled = false,
  onToolChange,
  selectedDeliveryTool,
  servicesEnabled,
  isFileDesignServiceEnabled = false,
  fileDesignService,
  handleServiceSelection,
  isFileDesignServiceSelected = false,
  fileChangeService,
  isFileChangeServiceEnabled = false,
  isFileChangeServiceSelected = false,
}: Props) => {
  const intl = useIntl();

  return (
    <Grid className="pd-grid-cols-2 pd-gap-4">
      {isAltUploadBeforeOrderEnabled && (
        <Grid.Item as={Fragment}>
          <DeliveryToolRadioCard
            title={intl.formatMessage({ id: 'delivery.tool.ownFiles', defaultMessage: 'I will deliver my own file(s)' })}
            free={servicesEnabled}
            onChange={onToolChange}
            active={selectedDeliveryTool === 'alternative-upload'}
            icon="file"
            value="alternative-upload"
          >
            <RichText>
              <ol>
                <li>
                  <FormattedMessage id="delivery.tool.uploader.step1" defaultMessage="Choose your product selection" />
                </li>
                <li>
                  <FormattedMessage id="delivery.tool.uploader.step2" defaultMessage="Deliver your files" />
                </li>
                <li>
                  <FormattedMessage id="delivery.tool.uploader.step3" defaultMessage="Place your order" />
                </li>
              </ol>
            </RichText>
          </DeliveryToolRadioCard>
        </Grid.Item>
      )}

      {isUploaderEnabled && (
        <Grid.Item as={Fragment}>
          <DeliveryToolRadioCard
            title={intl.formatMessage({ id: 'delivery.tool.ownFiles', defaultMessage: 'I will deliver my own file(s)' })}
            free={servicesEnabled}
            onChange={onToolChange}
            active={selectedDeliveryTool === DELIVERY_TOOLS.UPLOADER_TOOL}
            icon="file"
            value={DELIVERY_TOOLS.UPLOADER_TOOL}
          >
            <RichText>
              <ol>
                <li>
                  <FormattedMessage id="delivery.tool.uploader.step1" defaultMessage="Choose your product selection" />
                </li>
                <li>
                  <FormattedMessage id="delivery.tool.uploader.step2" defaultMessage="Deliver your files" />
                </li>
                <li>
                  <FormattedMessage id="delivery.tool.uploader.step3" defaultMessage="Place your order" />
                </li>
              </ol>
            </RichText>
          </DeliveryToolRadioCard>
        </Grid.Item>
      )}

      {isEditorEnabled && (
        <Grid.Item as={Fragment}>
          <DeliveryToolRadioCard
            title={intl.formatMessage({ id: 'delivery.tool.designOnline', defaultMessage: 'Design online' })}
            free={servicesEnabled}
            onChange={onToolChange}
            active={selectedDeliveryTool === 'editor'}
            icon="image"
            value="editor"
          >
            <RichText>
              <ol>
                <li>
                  <FormattedMessage id="delivery.tool.editor.step1" defaultMessage="Choose your product selection" />
                </li>
                <li>
                  <FormattedMessage id="delivery.tool.editor.step2" defaultMessage="Create your design" />
                </li>
                <li>
                  <FormattedMessage id="delivery.tool.editor.step3" defaultMessage="Place your order" />
                </li>
              </ol>
            </RichText>
          </DeliveryToolRadioCard>
        </Grid.Item>
      )}
      {isFileChangeServiceEnabled && fileChangeService && (
        <Grid.Item>
          <DeliveryToolRadioCard
            title={fileChangeService.displayName || fileChangeService.name}
            price={fileChangeService.price}
            onChange={handleServiceSelection}
            active={Boolean(isFileChangeServiceSelected)}
            icon="photo-edit"
            value={fileChangeService.key}
            labelText={intl.formatMessage({ id: 'services.label.new', defaultMessage: 'New' })}
            supportArticleId={fileChangeService.readMore?.contentful_id}
          >
            {fileChangeService.description && (
            <RichText>
              <Markdown>{fileChangeService.description.description}</Markdown>
            </RichText>
            )}
          </DeliveryToolRadioCard>
        </Grid.Item>
      )}
      { isFileDesignServiceEnabled && fileDesignService && (
        <FeatureToggle id="file_design_service_pre-order">
          <FeatureToggle.Enabled>
            <Grid.Item as={Fragment}>
              <DeliveryToolRadioCard
                title={fileDesignService.displayName || fileDesignService.name}
                price={fileDesignService.price}
                onChange={handleServiceSelection}
                active={Boolean(isFileDesignServiceSelected)}
                icon="designer"
                value={fileDesignService.key}
                labelText={intl.formatMessage({ id: 'services.label.new', defaultMessage: 'New' })}
                supportArticleId={fileDesignService.readMore?.contentful_id}
              >
                {fileDesignService.description && (
                  <RichText>
                    <Markdown>{fileDesignService.description.description}</Markdown>
                  </RichText>
                )}
              </DeliveryToolRadioCard>
            </Grid.Item>
          </FeatureToggle.Enabled>
        </FeatureToggle>
      )}
    </Grid>
  );
};

export default DetermineDeliveryTool;
